import {
  Box,
  Grid,
  Link,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { FaFilePdf } from "react-icons/fa6";
import { saveAs } from "file-saver";
import { useParams } from "react-router";
import { useEffect, useState } from "react";

import {
  DisciplineContainerStyle,
  DisciplineTableCell,
  TableMain,
  TableMainHead,
  TableMainHeadRow,
} from "../../AuthorizationDetails/Style/DisciplineStyle";
import fontWeight from "../../../styles/mui/fontWeight";
import { MessageType, NO_RECORD_FOUND } from "../../../constants/tostmsg";
import colors from "../../../styles/colors/colors";
import {
  DocumentDownloadDispatchTypes,
  DocumentListActionDispatchTypes,
  DocumentTabType,
  InboundDocumentationHeaderLabel,
} from "../../../constants/Document";
import { flexAlignCentre } from "../../../styles/mui/styles/display";
import { formatDate } from "../../../utils/DateTime";
import { DateFormats } from "../../../constants/Date";
import {
  DocumentDetailsTableCell,
  DocumentName,
  MainContainer,
} from "../styles/InboundDocumentStyle";
import { OuterBodyGrid } from "../styles/TabsStyle";
import { DocumentList } from "../../../models/AuthorizationDetailsTab";
import { DocumentDownloadResponse } from "../../../models/Api/AuthorizationDetailsTab";
import { resetStates } from "../../../actions/Common";
import SnackbarUtils from "../../../utils/SnackbarProvider";
import { ReferralResponseMessages } from "../../../constants/AllPatientRecords";
import { MarginRight } from "../styles/AuthorizationDetailsTabs";

import { decryptId } from "../../../utils";
export interface PropsFromState {
  documentListState: DocumentList[];
  documentDownloadData: DocumentDownloadResponse;
}
export interface PropsFromDispatch {
  getDocumentList(referralId: number, type : number): void;
  documentDownload: (blobId: number) => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

const InboundDocumentation: React.FC<AllProps> = ({ documentListState, getDocumentList, documentDownload, documentDownloadData }) => {
  const { response: documentDownloadResponse, error: documentDownloadError } =
    documentDownloadData;
  const [downloadRequest, setDownloadRequest] = useState<{
    blobId: number;
    fileName: string;
  } | null>(null);
  
   const { authNo } = useParams();
   const [referralId, setReferralId] = useState<number | null>(null);
   
   useEffect(() => {
     const loadReferralId = async () => {
       if (!authNo) {
         setReferralId(null);
         return;
       }

       const decryptedId = await decryptId(authNo);
        const parsed = Number(decryptedId);
        setReferralId(Number.isFinite(parsed) ? parsed : null);
     };

     void loadReferralId().catch(() => setReferralId(null));
   }, [authNo]);
  useEffect(() => {
    if(documentListState && referralId !== null) {
      getDocumentList(referralId, DocumentTabType.DOCUMENT);
    }
  }, [referralId]);

  useEffect(() => {
    // AB#26696: only handle download responses that were triggered from this tab instance.
    if (downloadRequest && documentDownloadResponse && documentDownloadResponse.blob) {
      const blob = new Blob([documentDownloadResponse.blob], {
        type: documentDownloadResponse.headers["content-type"],
      });
      saveAs(blob, downloadRequest.fileName, { autoBom: false });
      setDownloadRequest(null);
      resetStates([DocumentDownloadDispatchTypes.GET_DOCUMENT_DOWNLOAD_RESET]);
    } else if (
      downloadRequest &&
      documentDownloadError &&
      documentDownloadError.error &&
      Object.keys(documentDownloadError.error).length == 0
    ) {
      setDownloadRequest(null);
      SnackbarUtils.error(
        ReferralResponseMessages(MessageType.DOCUMENT_DOWNLOAD_ERROR)
      );
    }
  }, [documentDownloadResponse, documentDownloadError, downloadRequest]);
  
  const downloadFile = (blobId: number, fileName: string) => {
    setDownloadRequest({ blobId, fileName });
    documentDownload(blobId);
  };

   useEffect(() => {
    return () => {
      resetStates([
        DocumentListActionDispatchTypes.GET_DOCUMENT_LIST_RESET,
        DocumentDownloadDispatchTypes.GET_DOCUMENT_DOWNLOAD_RESET,
      ]);
    };
  }, []);
  
  const validation = () => {
    return (
      <TableRow>
        <TableCell colSpan={InboundDocumentationHeaderLabel.length}>
          <Box display="flex" justifyContent={"center"}>
            <Typography
              variant="subtitle2"
              fontWeight={fontWeight.Weight[4]}
              sx={DisciplineTableCell}
            >
              {NO_RECORD_FOUND}
            </Typography>
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  const documentTableCell = (value: string | number, date?: boolean) => {
    return (
      <>
        <TableCell sx={DocumentDetailsTableCell}>
          <Typography
            variant="subtitle1"
            fontWeight={date ? fontWeight.Weight[5] : fontWeight.Weight[6]}
            color={colors.fonts[4]}
          >
            {value}
          </Typography>
        </TableCell>
      </>
    );
  };

  return (
    <Grid container sx={OuterBodyGrid}>
      <Grid
        size={12}
        sx={MainContainer}
      >
        <Grid container>
          <Grid size={12}>
            <Typography
              variant="h4"
              fontWeight={fontWeight.Weight[7]}
              color={colors.fonts[5]}
              p={"1rem 0"}
            >
              Documentation Received With Requests
            </Typography>
          </Grid>
          <Grid size={12} p={"0 0.5rem "}>
            <TableContainer sx={DisciplineContainerStyle} component={Paper}>
              <TableMain>
                <TableMainHead>
                  <TableMainHeadRow>
                    {InboundDocumentationHeaderLabel &&
                      InboundDocumentationHeaderLabel.map((cell) => (
                        <TableCell width={cell.width} key={cell.label}>
                          <Typography
                            variant="subtitle2"
                            fontWeight={fontWeight.Weight[6]}
                            color={colors.fonts[4]}
                          >
                            {cell.label}
                          </Typography>
                        </TableCell>
                      ))}
                  </TableMainHeadRow>
                </TableMainHead>
                <TableBody>
                  {documentListState?.length ? (
                    documentListState.map((row: { blobId: number; documentName: string; requestedDateTime: string; documentTypeName: string | number;}) => (
                      <TableRow key={row.blobId}>
                        {/* Document Name */}
                        <TableCell>
                          <Box sx={flexAlignCentre}>
                            <FaFilePdf size="1rem" style={MarginRight()} />

                            <Tooltip title={row.documentName} arrow>
                              <Link
                                component="button"
                                underline="none"
                                onClick={() => {
                                  downloadFile(row.blobId, row.documentName);
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  fontWeight={fontWeight.Weight[5]}
                                  color={colors.fonts[18]}
                                  sx={DocumentName}
                                >
                                  {row.documentName}
                                </Typography>
                              </Link>
                            </Tooltip>
                          </Box>
                        </TableCell>

                        {/* Requested Date */}
                        {documentTableCell(
                          formatDate(
                            row.requestedDateTime,
                            DateFormats.MM_DD_YYYY_hh_mm_ss_a
                          ),
                          true
                        )}

                        {/* Document Type */}
                        {documentTableCell(row.documentTypeName)}
                      </TableRow>
                    ))
                  ) : (
                    validation()
                  )}
                </TableBody>
              </TableMain>
            </TableContainer>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default InboundDocumentation;
